import { useRef } from 'react';
import styled from 'styled-components';

/**
 * The analysed range drawn over a FilmStrip: a green frame with a white handle at each end. Drag a handle (or use
 * the arrow keys on it) to move that end. Values are seconds.
 */

const Frame = styled.span`
	position: absolute;
	top: -4px;
	bottom: -4px;
	border: 2px solid ${({ theme }) => theme.colors.green};
	border-radius: 8px;
	background: rgba(32, 172, 107, 0.1);
	pointer-events: none;
`;

const Shade = styled.span`
	position: absolute;
	top: 0;
	bottom: 0;
	background: rgba(8, 10, 8, 0.6);
	pointer-events: none;
`;

const Handle = styled.button`
	position: absolute;
	top: 50%;
	width: 14px;
	height: 34px;
	margin: -17px 0 0 -7px;
	border: 0;
	border-radius: 4px;
	background: #fff;
	box-shadow: 0 0 0 3px rgba(32, 172, 107, 0.45);
	cursor: ew-resize;
	touch-action: none;
	&:focus-visible {
		outline: 2px solid ${({ theme }) => theme.colors.lightGreen};
		outline-offset: 2px;
	}
`;

const MIN_SPAN = 1;

const RangeOverlay = ({ duration, start, end, onChange }) => {
	const ref = useRef(null);
	if (!duration) return null;
	const pct = s => `${(Math.max(0, Math.min(duration, s)) / duration) * 100}%`;
	const set = (which, s) => {
		const v = Math.round(Math.max(0, Math.min(duration, s)) * 100) / 100;
		if (which === 'start') onChange({ start: Math.min(v, end - MIN_SPAN), end });
		else onChange({ start, end: Math.max(v, start + MIN_SPAN) });
	};
	const drag = which => e => {
		const box = ref.current?.parentElement?.getBoundingClientRect();
		if (!box) return;
		e.currentTarget.setPointerCapture(e.pointerId);
		const move = ev => set(which, ((ev.clientX - box.left) / box.width) * duration);
		const up = () => {
			window.removeEventListener('pointermove', move);
			window.removeEventListener('pointerup', up);
		};
		window.addEventListener('pointermove', move);
		window.addEventListener('pointerup', up);
	};
	const keys = which => e => {
		const step = e.shiftKey ? 5 : 0.5;
		const now = which === 'start' ? start : end;
		if (e.key === 'ArrowLeft') set(which, now - step);
		else if (e.key === 'ArrowRight') set(which, now + step);
		else return;
		e.preventDefault();
	};
	return (
		<span ref={ref} style={{ display: 'contents' }}>
			<Shade style={{ left: 0, width: pct(start) }} />
			<Shade style={{ left: pct(end), right: 0 }} />
			<Frame style={{ left: pct(start), width: `calc(${pct(end)} - ${pct(start)})` }} />
			{['start', 'end'].map(which => (
				<Handle
					key={which}
					type="button"
					role="slider"
					aria-label={
						which === 'start'
							? 'Start of the analyzed part'
							: 'End of the analyzed part'
					}
					aria-valuemin={0}
					aria-valuemax={Math.round(duration)}
					aria-valuenow={Math.round(which === 'start' ? start : end)}
					style={{ left: pct(which === 'start' ? start : end) }}
					onPointerDown={drag(which)}
					onKeyDown={keys(which)}
				/>
			))}
		</span>
	);
};

const DoneFill = styled.span`
	position: absolute;
	top: 0;
	bottom: 0;
	background: rgba(32, 172, 107, 0.16);
	border-right: 2px solid ${({ theme }) => theme.colors.green};
	box-shadow: 4px 0 12px -4px ${({ theme }) => theme.colors.green};
	pointer-events: none;
	transition: width 1s linear;
	&::after {
		content: '';
		position: absolute;
		left: 0;
		right: 0;
		bottom: 0;
		height: 4px;
		background: ${({ theme }) => theme.colors.green};
	}
`;

// the frames not reached yet: grey and darker, so the strip reads as a bar filling from the left
const Pending = styled.span`
	position: absolute;
	top: 0;
	bottom: 0;
	background: rgba(8, 10, 8, 0.35);
	backdrop-filter: grayscale(1);
	pointer-events: none;
	transition:
		left 1s linear,
		width 1s linear;
`;

/**
 * Live analysis progress over a FilmStrip: the part outside the analysed range is dimmed, the frames already done
 * keep their colour under a green fill with a bar along the bottom, and the frames still to come are grey.
 * `frac` is 0-1 of the analysed range.
 */
export const ProcessedOverlay = ({ duration, start = 0, end, frac }) => {
	if (!duration) return null;
	const to = end ?? duration;
	const pct = s => `${(Math.max(0, Math.min(duration, s)) / duration) * 100}%`;
	const doneTo = start + (to - start) * Math.max(0, Math.min(1, frac || 0));
	return (
		<span style={{ display: 'contents' }} aria-hidden="true">
			<Shade style={{ left: 0, width: pct(start) }} />
			<Shade style={{ left: pct(to), right: 0 }} />
			<Pending style={{ left: pct(doneTo), width: `calc(${pct(to)} - ${pct(doneTo)})` }} />
			<DoneFill style={{ left: pct(start), width: `calc(${pct(doneTo)} - ${pct(start)})` }} />
		</span>
	);
};

export default RangeOverlay;
